import React, { useState } from 'react'
import { COMPANY_INFO } from '../lib/constants'

export default function ContactFormMinimal() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '', // Mandatory honeypot field
  })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!res.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.')
      }

      setStatus('success')
      setFormData({
        navn: '',
        telefon: '',
        email: '',
        beskrivelse: '',
        website_url: '',
      })
    } catch (err) {
      console.error('Submission error:', err)
      setStatus('error')
      setErrorMessage(err.message || 'Der opstod en uventet fejl. Kontakt os venligst pr. telefon.')
    }
  }

  if (status === 'success') {
    return (
      <div className="bg-[#18191D]/90 border border-white/20 rounded-2xl p-8 text-center space-y-3">
        <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto text-xl">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h5 className="text-lg font-bold text-white">Tak for din henvendelse!</h5>
        <p className="text-sm text-[#D9D9D5]">
          Jakob har modtaget din besked og vender tilbage hurtigst muligt. Haster opgaven, ring gerne direkte på {COMPANY_INFO.phone}.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* MANDATORY ANTISPAM HONEYPOT - ABSOLUTELY HIDDEN */}
      <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url_min">Website URL</label>
        <input
          type="text"
          id="website_url_min"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website_url}
          onChange={handleChange}
        />
      </div>

      {status === 'error' && (
        <div className="p-3 bg-rose-500/20 border border-rose-500/40 rounded-xl text-xs sm:text-sm text-rose-200 flex items-start gap-2.5">
          <i className="fa-solid fa-circle-exclamation text-rose-400 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Top Row: 50/50 Grid for Name and Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="navn_min" className="block text-xs uppercase tracking-wider font-semibold text-[#D9D9D5] mb-1">
            Navn <span className="text-[#E36C0A]">*</span>
          </label>
          <input
            type="text"
            id="navn_min"
            name="navn"
            required
            placeholder="Dit fulde navn"
            value={formData.navn}
            onChange={handleChange}
            className="w-full bg-transparent border-0 border-b border-white/30 focus:border-[#E36C0A] focus:ring-0 px-0 py-2.5 text-white placeholder:text-white/40 text-sm transition-colors"
          />
        </div>

        <div>
          <label htmlFor="email_min" className="block text-xs uppercase tracking-wider font-semibold text-[#D9D9D5] mb-1">
            Email <span className="text-[#E36C0A]">*</span>
          </label>
          <input
            type="email"
            id="email_min"
            name="email"
            required
            placeholder="din@email.dk"
            value={formData.email}
            onChange={handleChange}
            className="w-full bg-transparent border-0 border-b border-white/30 focus:border-[#E36C0A] focus:ring-0 px-0 py-2.5 text-white placeholder:text-white/40 text-sm transition-colors"
          />
        </div>
      </div>

      {/* Full width: Phone */}
      <div>
        <label htmlFor="telefon_min" className="block text-xs uppercase tracking-wider font-semibold text-[#D9D9D5] mb-1">
          Telefonnummer <span className="text-[#E36C0A]">*</span>
        </label>
        <input
          type="tel"
          id="telefon_min"
          name="telefon"
          required
          placeholder="22 32 99 79"
          value={formData.telefon}
          onChange={handleChange}
          className="w-full bg-transparent border-0 border-b border-white/30 focus:border-[#E36C0A] focus:ring-0 px-0 py-2.5 text-white placeholder:text-white/40 text-sm transition-colors"
        />
      </div>

      {/* Full width: Message */}
      <div>
        <label htmlFor="beskrivelse_min" className="block text-xs uppercase tracking-wider font-semibold text-[#D9D9D5] mb-1">
          Beskrivelse af opgaven <span className="text-[#E36C0A]">*</span>
        </label>
        <textarea
          id="beskrivelse_min"
          name="beskrivelse"
          required
          rows={3}
          placeholder="Beskriv kort hvad du har brug for hjælp til..."
          value={formData.beskrivelse}
          onChange={handleChange}
          className="w-full bg-transparent border-0 border-b border-white/30 focus:border-[#E36C0A] focus:ring-0 px-0 py-2.5 text-white placeholder:text-white/40 text-sm resize-none transition-colors"
        />
      </div>

      {/* Solid pill-shaped primary CTA button spanning full width */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full py-4 rounded-full bg-[#E36C0A] hover:bg-[#B95000] text-white font-bold text-xs uppercase tracking-widest transition-all shadow-lg hover:shadow-xl active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {status === 'submitting' ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>Sender forespørgsel...</span>
            </>
          ) : (
            <>
              <span>Send forespørgsel</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  )
}
